<template>
	<layout-default class="layout-order" v-bind="layoutProps">
		<template #header-tools>
			<div class="layout-order__title">
				{{ headline }}
			</div>

			<div class="layout-order__nav">
				<nav-tabs :items="navItems" />
			</div>
		</template>

		<slot />
	</layout-default>
</template>

<script lang="ts">
import { pick } from 'lodash';
import { defineComponent, PropType } from 'vue';
import { LocationAsRelativeRaw, RouteLocationNormalizedLoaded } from 'vue-router';

import NavTabs from '@/components/nav/NavTabs.vue';
import { OrderStep } from '@/constants/order/constants';
import { getOrderSectionsNav, OrderSectionsFlow } from '@/constants/order/nav/constants';
import { OrderModel } from '@/store/models/Order';
import { OrderSectionNavTabModel } from '@/store/models/OrderNavTabsItem';
import { orderGetters } from '@/store/modules/order';
import { isOrderRejected } from '@/store/modules/order/types';
import { AnyObject } from '@/tools/types';

import LayoutDefault from './LayoutDefault.vue';

export default defineComponent({
	components: {
		LayoutDefault,
		NavTabs,
	},

	props: {
		resolveOrderGuid: {
			type: Function as PropType<
				(self: { $route: RouteLocationNormalizedLoaded }) => string | null
			>,
			required: true,
		},
	},

	computed: {
		layoutProps(): AnyObject {
			return {
				headerOptions: {
					showUserName: false,
				},
				...pick(this.$props, ['sidebar', 'headerOptions']),
			};
		},

		order(): OrderModel | undefined {
			const orderGuid = this.resolveOrderGuid(this);

			return orderGuid ? orderGetters.getOrderByGuid()(orderGuid) : undefined;
		},

		flow(): OrderSectionsFlow {
			if (this.order && isOrderRejected(this.order.order_status!)) {
				return OrderSectionsFlow.Edit;
			}

			if (this.order?.order_status == 'accepted_waiting_for_feedback') {
				return OrderSectionsFlow.SocketFitting;
			} else if (this.$route.meta?.flow === 'prosthetist') {
				return OrderSectionsFlow.Prosthetist;
			} else {
				return OrderSectionsFlow.Customization;
			}
		},

		headline(): string {
			const { order } = this;

			if (!order?.order_number) {
				return 'Creating New Order';
			}

			if (typeof this.$route.meta?.headline === 'function') {
				return this.$route.meta.headline(order.order_number);
			}

			return `Creating New Order #${order.order_number}`;
		},

		navItems(): Array<OrderSectionNavTabModel> {
			// FIXME: remove non-null assertion operator
			const orderGuid = this.resolveOrderGuid(this)!;

			// if (!orderGuid) return [];

			const order = orderGetters.getOrderByGuid()(orderGuid);
			const sections = getOrderSectionsNav(orderGuid, this.flow);

			return sections.map(section => {
				if ((section.to as LocationAsRelativeRaw).name === OrderStep.New) {
					return section;
				}

				section.is_disabled = !order || this.isSectionAfterOrderInPath(section, order);

				return section;
			});
		},
	},

	methods: {
		isSectionAfterOrderInPath(section: OrderSectionNavTabModel, order: OrderModel): boolean {
			return section.steps.every(step => OrderStep.compareStepToOrder(step, '>', order));
		},
	},
});
</script>

<style lang="scss">
.layout-order {
	&__title {
		grid-column: 3 / span 5;
		align-self: center;
		font-size: 1.5rem;

		@include max-xl {
			grid-column: 3 / span 4;
		}

		@include max-lg-and-portrait {
			margin-left: -1.8rem;
		}

		@include max-md-and-portrait {
			display: none;
		}
	}

	&__nav {
		grid-column: 9 / span 14;
		align-self: center;

		@include max-xl {
			grid-column: 7 / span 16;
		}

		@include max-md-and-portrait {
			grid-column: 3 / span 20;
		}
	}
}
</style>
